import { useEffect, useRef } from "react";

const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const KEYBOARD_OPEN_THRESHOLD = 120;
const EDITABLE_SELECTOR = [
  'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="hidden"]):not([disabled])',
  "textarea:not([disabled])",
  "select:not([disabled])",
  '[contenteditable="true"]',
].join(", ");

const isEditableElement = (element: EventTarget | null): element is HTMLElement => {
  return element instanceof HTMLElement && element.matches(EDITABLE_SELECTOR);
};

const isMobileViewport = () => window.matchMedia(MOBILE_MEDIA_QUERY).matches;

const getViewportMetrics = () => {
  const visualViewport = window.visualViewport;
  const viewportHeight = visualViewport?.height ?? window.innerHeight;
  const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
  const stableViewportHeight = Math.max(viewportHeight + viewportOffsetTop, window.innerHeight);

  return { viewportHeight, viewportOffsetTop, stableViewportHeight };
};

const applyViewportState = (baselineViewportHeightRef: { current: number }) => {
  const root = document.documentElement;
  const body = document.body;
  const { viewportHeight, viewportOffsetTop, stableViewportHeight } = getViewportMetrics();
  const editableActive = isEditableElement(document.activeElement);

  if (!isMobileViewport()) {
    baselineViewportHeightRef.current = stableViewportHeight;
  } else if (!editableActive) {
    baselineViewportHeightRef.current = stableViewportHeight;
  } else if (baselineViewportHeightRef.current === 0) {
    baselineViewportHeightRef.current = stableViewportHeight;
  } else {
    baselineViewportHeightRef.current = Math.max(baselineViewportHeightRef.current, stableViewportHeight);
  }

  const baselineViewportHeight = baselineViewportHeightRef.current || stableViewportHeight;
  const keyboardInset = Math.max(0, baselineViewportHeight - (viewportHeight + viewportOffsetTop));
  const keyboardOpen = isMobileViewport() && editableActive && keyboardInset > KEYBOARD_OPEN_THRESHOLD;

  root.style.setProperty("--visual-viewport-height", `${viewportHeight}px`);
  root.style.setProperty("--keyboard-inset-height", `${keyboardOpen ? keyboardInset : 0}px`);
  root.classList.toggle("keyboard-open", keyboardOpen);
  body.classList.toggle("keyboard-open", keyboardOpen);
  root.dataset.keyboardOpen = keyboardOpen ? "true" : "false";
};

const revealFocusedElement = (element: HTMLElement | null) => {
  if (!isEditableElement(element) || !isMobileViewport()) return;

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });

      const scrollContainer = element.closest(
        "[data-keyboard-scroll-area='true'], [data-radix-scroll-area-viewport], .overflow-y-auto, .overflow-auto"
      ) as HTMLElement | null;

      if (!scrollContainer) return;

      const targetRect = element.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const bottomClearance = 24;
      const topClearance = 16;

      if (targetRect.bottom > containerRect.bottom - bottomClearance) {
        scrollContainer.scrollTop += targetRect.bottom - (containerRect.bottom - bottomClearance);
        return;
      }

      if (targetRect.top < containerRect.top + topClearance) {
        scrollContainer.scrollTop -= (containerRect.top + topClearance) - targetRect.top;
      }
    });
  });
};

const MobileViewportManager = () => {
  const baselineViewportHeightRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let viewportTimer: number | undefined;

    const scheduleViewportUpdate = (delay = 0) => {
      window.clearTimeout(viewportTimer);
      viewportTimer = window.setTimeout(() => {
        applyViewportState(baselineViewportHeightRef);
        revealFocusedElement(document.activeElement as HTMLElement | null);
      }, delay);
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isEditableElement(event.target)) return;
      applyViewportState(baselineViewportHeightRef);
      scheduleViewportUpdate(80);
    };

    const handleFocusOut = () => {
      scheduleViewportUpdate(120);
    };

    const handleViewportChange = () => {
      scheduleViewportUpdate(0);
    };

    applyViewportState(baselineViewportHeightRef);

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);
    window.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("scroll", handleViewportChange);

    return () => {
      window.clearTimeout(viewportTimer);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      window.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);

      const root = document.documentElement;
      const body = document.body;
      root.classList.remove("keyboard-open");
      body.classList.remove("keyboard-open");
      delete root.dataset.keyboardOpen;
      root.style.removeProperty("--visual-viewport-height");
      root.style.removeProperty("--keyboard-inset-height");
    };
  }, []);

  return null;
};

export default MobileViewportManager;